/**
 * Tidy: a workflow laid out top to bottom with ELK's layered algorithm,
 * Start first, at the canvas's card size and spacing. One rule for the page's
 * Tidy, a chart saved left to right before AMO-177, and the server's layout
 * after eve adds or removes steps, so every viewer sees the same chart.
 * Each runtime brings its own ELK: the page loads it on first use, the
 * server once.
 *
 * @module workflowLayout
 */
import type { WorkflowEditGraph, WorkflowEditNode } from "./workflowEdit.ts";

/** A step card as the canvas draws it, and the gap between rows. */
export const WORKFLOW_CARD = { width: 400, height: 121, gap: 80 } as const;

const LAYOUT_OPTIONS = {
  "elk.algorithm": "layered",
  "elk.direction": "DOWN",
  "elk.layered.spacing.nodeNodeBetweenLayers": String(WORKFLOW_CARD.gap),
  "elk.spacing.nodeNode": "40",
  "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
  "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
} as const;

/** The part of an `elkjs` instance a tidy uses. */
export interface ElkEngine {
  layout(graph: unknown): Promise<{ readonly children?: ReadonlyArray<{ readonly id: string; readonly x?: number; readonly y?: number }> }>;
}

/** Whether a graph is still laid out left to right (saved before AMO-177). */
export const needsRelayout = (graph: WorkflowEditGraph): boolean => graph.layout !== "down" && graph.nodes.length > 0;

/**
 * The graph with every step moved to its laid-out place, marked as laid out
 * top to bottom; links are untouched. `heights` are the cards as drawn, where
 * a page knows them.
 */
export async function tidyWorkflowGraph<N extends WorkflowEditNode, G extends WorkflowEditGraph<N>>(
  elk: ElkEngine,
  graph: G,
  heights: Readonly<Record<string, number>> = {},
): Promise<G> {
  if (graph.nodes.length === 0) return { ...graph, layout: "down" };
  const result = await elk.layout({
    id: "root",
    layoutOptions: LAYOUT_OPTIONS,
    children: graph.nodes.map((node) => ({
      id: node.id,
      width: WORKFLOW_CARD.width,
      height: heights[node.id] ?? WORKFLOW_CARD.height,
      ...(node.type === "start" ? { layoutOptions: { "elk.layered.layering.layerConstraint": "FIRST" } } : {}),
    })),
    edges: graph.edges.map((edge) => ({ id: edge.id, sources: [edge.source], targets: [edge.target] })),
  });
  const positions = new Map((result.children ?? []).map((child) => [child.id, { x: Math.round(child.x ?? 0), y: Math.round(child.y ?? 0) }]));
  return { ...graph, nodes: graph.nodes.map((node) => ({ ...node, position: positions.get(node.id) ?? node.position })), layout: "down" };
}
